import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  BadgeCheck,
  CalendarDays,
  ChevronDown,
  Link2,
  MessageSquare,
  MousePointerClick,
  Newspaper,
  Tent,
  UserPlus,
  Users,
} from "lucide-react";
import { Button as TamaguiButton, XStack, YStack } from "tamagui";

import { supabase } from "@/integrations/supabase/client";
import { AdminMetricCard, AdminPageScaffold, AdminStatusPill } from "@/components/AdminScaffold";
import { getRecentMemberActivityCutoffIso } from "../../shared/activities";
import {
  type AdminProductAnalyticsOverview,
  describeContentHealthCard,
  formatContentHealthDate,
  formatKindredAnalyticsLabel,
  getContentHealthSummary,
  normalizeAdminProductAnalyticsOverview,
  type ContentHealthSummary,
} from "../../shared/system";
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppLoadingState,
  AppSection,
  AppSectionHeader,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

const ADMIN_DASHBOARD_TIMEOUT_MS = 12000;

const isAdminDashboardTimeout = (error: unknown) =>
  error instanceof Error && error.message.includes("Admin dashboard timed out");

const withAdminDashboardTimeout = async <T,>(operation: Promise<T>): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  try {
    return await Promise.race([
      operation,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(`Admin dashboard timed out after ${ADMIN_DASHBOARD_TIMEOUT_MS}ms`));
        }, ADMIN_DASHBOARD_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [stats, setStats] = useState({
    members: 0,
    adventures: 0,
    channelPosts: 0,
    kinfeedPosts: 0,
    events: 0,
    openDeletionRequests: 0,
    openLoginSupportRequests: 0,
    pendingReports: 0,
    newMembers7d: 0,
    connectedPairs: 0,
    onboardingCompleted: 0,
    memberEventDrafts: 0,
    recentMemberActivities: 0,
  });
  const [contentHealth, setContentHealth] = useState<ContentHealthSummary>(getContentHealthSummary([]));
  const [analytics, setAnalytics] = useState<AdminProductAnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const recentActivityCutoff = getRecentMemberActivityCutoffIso();
      const [
        { count: members },
        { count: adventures },
        { count: channelPosts },
        { count: kinfeedPosts },
        { count: events },
        { count: openDeletionRequests },
        { count: openLoginSupportRequests },
        { count: pendingReports },
        { count: newMembers7d },
        { count: connectedPairs },
        { count: onboardingCompleted },
        { count: memberEventDrafts },
        { count: recentMemberActivities },
        { data: contentCards },
        analyticsResult,
      ] = await withAdminDashboardTimeout(
        Promise.all([
          supabase.from("profiles").select("id", { count: "exact", head: true }),
          supabase.from("adventures").select("id", { count: "exact", head: true }),
          supabase.from("posts").select("id", { count: "exact", head: true }),
          supabase.from("feed_posts").select("id", { count: "exact", head: true }),
          supabase.from("events").select("id", { count: "exact", head: true }).eq("is_published", true),
          supabase
            .from("account_deletion_requests")
            .select("id", { count: "exact", head: true })
            .in("status", ["pending", "in_review"]),
          supabase
            .from("auth_support_requests")
            .select("id", { count: "exact", head: true })
            .in("status", ["open", "in_progress"]),
          supabase.from("reports").select("id", { count: "exact", head: true }).eq("status", "pending"),
          supabase
            .from("profiles")
            .select("id", { count: "exact", head: true })
            .gte("created_at", new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()),
          supabase.from("connections").select("id", { count: "exact", head: true }).eq("status", "connected"),
          supabase.from("profiles").select("id", { count: "exact", head: true }).eq("onboarding_completed", true),
          supabase
            .from("events")
            .select("id", { count: "exact", head: true })
            .eq("is_published", false),
          supabase
            .from("adventures")
            .select("id", { count: "exact", head: true })
            .eq("status", "active")
            .gte("created_at", recentActivityCutoff),
          supabase.from("content_cards").select("id, title, section, category, last_reviewed_at, is_active"),
          supabase.rpc("get_admin_product_analytics" as any),
        ]),
      );

      if (analyticsResult.error) {
        console.warn("Could not load admin product analytics", analyticsResult.error);
      }
      const normalizedAnalytics = analyticsResult.error
        ? null
        : normalizeAdminProductAnalyticsOverview(analyticsResult.data);

      setStats({
        members: members ?? 0,
        adventures: adventures ?? 0,
        channelPosts: channelPosts ?? 0,
        kinfeedPosts: kinfeedPosts ?? 0,
        events: events ?? 0,
        openDeletionRequests: openDeletionRequests ?? 0,
        openLoginSupportRequests: openLoginSupportRequests ?? 0,
        pendingReports: pendingReports ?? 0,
        newMembers7d: newMembers7d ?? 0,
        connectedPairs: connectedPairs ?? 0,
        onboardingCompleted: onboardingCompleted ?? 0,
        memberEventDrafts: memberEventDrafts ?? 0,
        recentMemberActivities: recentMemberActivities ?? 0,
      });
      setContentHealth(getContentHealthSummary(contentCards ?? []));
      setAnalytics(normalizedAnalytics);
    } catch (error) {
      console.warn("Could not load admin dashboard", error);
      setLoadError(
        isAdminDashboardTimeout(error)
          ? "Admin tools are taking too long to respond. Please try again in a moment."
          : "Admin tools could not load just now. Please try again in a moment.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchDashboard();
  }, [fetchDashboard]);

  const statCards = [
    { label: "Total members", value: stats.members, icon: Users, onPress: () => navigate("/admin/members") },
    { label: "Activities", value: stats.adventures, icon: Tent },
    { label: "Channel posts", value: stats.channelPosts, icon: MessageSquare },
    { label: "Kinfeed posts", value: stats.kinfeedPosts, icon: Newspaper },
    { label: "Events published", value: stats.events, icon: CalendarDays },
    { label: "New members 7d", value: stats.newMembers7d, icon: UserPlus },
    { label: "Connected pairs", value: stats.connectedPairs, icon: Link2 },
    { label: "Onboarding completed", value: stats.onboardingCompleted, icon: BadgeCheck },
  ];

  const attentionItems = [
    {
      title: "Login support requests",
      count: stats.openLoginSupportRequests,
      summary:
        stats.openLoginSupportRequests > 0
          ? `${stats.openLoginSupportRequests} people are waiting for help getting into Kindred.`
          : "No one is currently waiting for login help.",
      path: "/admin/login-support",
      actionLabel: "Reply",
    },
    {
      title: "Event review queue",
      count: stats.memberEventDrafts,
      summary:
        stats.memberEventDrafts > 0
          ? `${stats.memberEventDrafts} events waiting for approval.`
          : "No events are waiting for approval right now.",
      path: "/admin/events",
      actionLabel: "Review",
    },
    {
      title: "Member activity queue",
      count: stats.recentMemberActivities,
      summary:
        stats.recentMemberActivities > 0
          ? `${stats.recentMemberActivities} newly created activities are already live and ready for a quick admin check.`
          : "No newly created member activities are waiting for a quick admin check right now.",
      path: "/admin/activities",
      actionLabel: "Open",
    },
    {
      title: "Moderation queue",
      count: stats.pendingReports,
      summary:
        stats.pendingReports > 0
          ? `${stats.pendingReports} reports still need a moderation decision.`
          : "No reports are currently waiting for moderation.",
      path: "/admin/moderation",
      actionLabel: "Open",
    },
    {
      title: "Content review due",
      count: contentHealth.dueNowCount,
      summary:
        contentHealth.dueNowCount > 0
          ? `${contentHealth.dueNowCount} support cards are due for review now.`
          : "No content cards are due for review right now.",
      path: "/admin/content",
      actionLabel: "Review",
    },
    {
      title: "Account deletion requests",
      count: stats.openDeletionRequests,
      summary:
        stats.openDeletionRequests > 0
          ? `${stats.openDeletionRequests} deletion requests are still open.`
          : "No open account deletion requests right now.",
      path: "/admin/account-deletion",
      actionLabel: "Open",
    },
  ];

  const attentionItemsOrdered = [...attentionItems].sort((left, right) => {
    const leftHasWork = (left.count ?? 0) > 0 ? 1 : 0;
    const rightHasWork = (right.count ?? 0) > 0 ? 1 : 0;

    if (leftHasWork !== rightHasWork) {
      return rightHasWork - leftHasWork;
    }

    return (right.count ?? 0) - (left.count ?? 0);
  });
  const activeAttentionItems = attentionItemsOrdered.filter((item) => (item.count ?? 0) > 0);
  const primaryAttentionItem = activeAttentionItems[0] ?? attentionItemsOrdered[0] ?? null;
  const totalAttentionCount = activeAttentionItems.reduce((sum, item) => sum + (item.count ?? 0), 0);

  const coreTools = [
    {
      title: "Manage members",
      summary: "Profiles, role assignments, and member history.",
      path: "/admin/members",
      actionLabel: "Open",
    },
    {
      title: "Content library",
      summary: "Support cards, announcements, and review cadence.",
      path: "/admin/content",
      actionLabel: "Open",
    },
  ];

  const referenceTools = [
    {
      title: "Admin & moderation guidance",
      summary: "Internal roles, escalation rules, and moderation boundaries.",
      path: "/moderation-guidelines",
      actionLabel: "Read",
    },
  ];

  const productAnalyticsCards = analytics
    ? [
        { label: "Active members 7d", value: analytics.activeMembers7d, icon: Users, detail: `${analytics.activeMembers30d} in 30 days` },
        { label: "Screen views 7d", value: analytics.screenViews7d, icon: Activity, detail: `${analytics.screenViews30d} in 30 days` },
        { label: "Sign-ins 7d", value: analytics.signIns7d, icon: BadgeCheck, detail: `${analytics.onboardingCompletionRate7d}% onboarding conversion` },
        { label: "Support opens 7d", value: analytics.supportCardOpens7d, icon: MousePointerClick, detail: "Helper packs and support links opened" },
        { label: "Chat room opens 7d", value: analytics.chatRoomOpens7d, icon: MessageSquare, detail: `${analytics.chatRoomMessages7d} messages sent` },
        { label: "Direct messages 7d", value: analytics.directMessages7d, icon: Link2, detail: `${analytics.channelJoins7d} channel joins` },
        { label: "Adventure joins 7d", value: analytics.adventureJoins7d, icon: Tent, detail: `${analytics.eventRsvps7d} event RSVPs` },
        { label: "Member event drafts 7d", value: analytics.memberEventDrafts7d, icon: CalendarDays, detail: `${analytics.socialLinkClicks7d} social link clicks` },
      ]
    : [];

  const highlightTitle =
    contentHealth.highlightCards.length === 0
      ? null
      : contentHealth.dueNowCount > 0
        ? "Cards needing attention now"
        : "Next reviews coming up";
  const toggleSection = (sectionId: string) => {
    setOpenSections((current) => ({ ...current, [sectionId]: !current[sectionId] }));
  };

  return (
    <AdminPageScaffold title="Admin">
      {loading ? (
        <AppLoadingState
          title="Loading admin overview"
          body="Pulling the latest counts, content health, and moderation shortcuts."
        />
      ) : loadError ? (
        <AppCard>
          <AppCardBody gap="$md">
            <AppSectionHeader
              title="Admin tools are taking a moment"
              subtitle={loadError}
            />
            <XStack>
              <AppButton type="button" tone="outlinePrimary" onPress={() => void fetchDashboard()}>
                Try again
              </AppButton>
            </XStack>
          </AppCardBody>
        </AppCard>
      ) : (
        <>
          {primaryAttentionItem ? (
            <AdminHomeSectionCard
              sectionId="priority"
              eyebrow="Priority"
              title={activeAttentionItems.length > 0 ? "Start here" : "All clear"}
              subtitle={
                activeAttentionItems.length > 0
                  ? `${totalAttentionCount} admin items are waiting across ${activeAttentionItems.length} queues. Open the top queue first, then work down.`
                  : "Nothing is waiting right now. The most likely next job is event review when fresh imports land."
              }
              accented
              isOpen={Boolean(openSections.priority)}
              onToggle={() => toggleSection("priority")}
            >
              <AppCardBody>
                <AdminActionRow
                  title={primaryAttentionItem.title}
                  summary={primaryAttentionItem.summary}
                  count={primaryAttentionItem.count}
                  actionLabel={primaryAttentionItem.actionLabel}
                  onPress={() => navigate(primaryAttentionItem.path)}
                />
              </AppCardBody>
            </AdminHomeSectionCard>
          ) : null}

          <AdminHomeSectionCard
            sectionId="operational-snapshot"
            eyebrow="Ops"
            title="Operational snapshot"
            subtitle="Passive totals for the shared member space, kept just below the priority item for quick context."
            isOpen={Boolean(openSections["operational-snapshot"])}
            onToggle={() => toggleSection("operational-snapshot")}
          >
            <AppCardBody>
              <XStack flexWrap="wrap" gap="$sm">
                {statCards.map((card) => (
                  <AdminMetricCard
                    key={card.label}
                    label={card.label}
                    value={card.value}
                    icon={card.icon}
                    onPress={card.onPress}
                  />
                ))}
              </XStack>
            </AppCardBody>
          </AdminHomeSectionCard>

          <AdminHomeSectionCard
            sectionId="queues"
            eyebrow="Queues"
            title="Needs attention"
            subtitle="Frequent admin jobs shown in working order, so the page reads like an operator queue rather than a static dashboard."
            isOpen={Boolean(openSections.queues)}
            onToggle={() => toggleSection("queues")}
          >
            <AppCardBody gap="$sm">
              {attentionItemsOrdered.map((item) => (
                <AdminActionRow
                  key={item.path}
                  title={item.title}
                  summary={item.summary}
                  count={item.count}
                  actionLabel={item.actionLabel}
                  onPress={() => navigate(item.path)}
                />
              ))}
            </AppCardBody>
          </AdminHomeSectionCard>

          <XStack flexWrap="wrap" gap="$sm">
            <AdminHomeSectionCard
              sectionId="tools"
              eyebrow="Tools"
              title="Core admin tools"
              subtitle="Open the main member and content areas directly without digging through the queue."
              minWidth={320}
              isOpen={Boolean(openSections.tools)}
              onToggle={() => toggleSection("tools")}
            >
              <AppCardBody gap="$sm">
                {coreTools.map((tool) => (
                  <AdminActionRow
                    key={tool.path}
                    title={tool.title}
                    summary={tool.summary}
                    actionLabel={tool.actionLabel}
                    onPress={() => navigate(tool.path)}
                  />
                ))}
              </AppCardBody>
            </AdminHomeSectionCard>

            <AdminHomeSectionCard
              sectionId="reference"
              eyebrow="Reference"
              title="Guidance"
              subtitle="Keep policy and escalation notes visible, but outside the live work lane."
              minWidth={320}
              isOpen={Boolean(openSections.reference)}
              onToggle={() => toggleSection("reference")}
            >
              <AppCardBody gap="$sm">
                {referenceTools.map((tool) => (
                  <AdminActionRow
                    key={tool.path}
                    title={tool.title}
                    summary={tool.summary}
                    actionLabel={tool.actionLabel}
                    onPress={() => navigate(tool.path)}
                  />
                ))}
              </AppCardBody>
            </AdminHomeSectionCard>
          </XStack>

          <AdminHomeSectionCard
            sectionId="usage-signals"
            eyebrow="Ops"
            title="Usage signals"
            subtitle="See which flows are gaining traction without letting analytics compete with the live queue."
            isOpen={Boolean(openSections["usage-signals"])}
            onToggle={() => toggleSection("usage-signals")}
          >
            <AppCardBody gap="$md">
              {analytics ? (
                <>
                  <XStack flexWrap="wrap" gap="$sm">
                    {productAnalyticsCards.map((card) => (
                      <AdminMetricCard
                        key={card.label}
                        label={card.label}
                        value={card.value}
                        icon={card.icon}
                        detail={card.detail}
                      />
                    ))}
                  </XStack>

                  <XStack flexWrap="wrap" gap="$sm">
                    <AppCard flexBasis={0} flexGrow={1} minWidth={260} backgroundColor="$background">
                      <AppCardBody gap="$sm">
                        <AppSectionHeader
                          title="Top screens this week"
                          subtitle="What members are actually opening most often."
                        />
                        <YStack gap="$sm">
                          {analytics.topScreens7d.length ? (
                            analytics.topScreens7d.map((item) => (
                              <XStack key={item.label} alignItems="center" justifyContent="space-between" gap="$sm">
                                <AppText fontSize="$sm" lineHeight="$sm">
                                  {formatKindredAnalyticsLabel(item.label)}
                                </AppText>
                                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                                  {item.count}
                                </AppText>
                              </XStack>
                            ))
                          ) : (
                            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                              No screen-view data yet.
                            </AppText>
                          )}
                        </YStack>
                      </AppCardBody>
                    </AppCard>

                    <AppCard flexBasis={0} flexGrow={1} minWidth={260} backgroundColor="$background">
                      <AppCardBody gap="$sm">
                        <AppSectionHeader
                          title="Top actions this week"
                          subtitle="The strongest behaviour signals beyond page views."
                        />
                        <YStack gap="$sm">
                          {analytics.topActions7d.length ? (
                            analytics.topActions7d.map((item) => (
                              <XStack key={item.label} alignItems="center" justifyContent="space-between" gap="$sm">
                                <AppText fontSize="$sm" lineHeight="$sm">
                                  {formatKindredAnalyticsLabel(item.label)}
                                </AppText>
                                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                                  {item.count}
                                </AppText>
                              </XStack>
                            ))
                          ) : (
                            <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                              No action data yet.
                            </AppText>
                          )}
                        </YStack>
                      </AppCardBody>
                    </AppCard>
                  </XStack>
                </>
              ) : (
                <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                  Analytics are warming up. Once members start using the newer tracked flows, this section will show where they return, message, join, and drop off.
                </AppText>
              )}
            </AppCardBody>
          </AdminHomeSectionCard>

          <AdminHomeSectionCard
            sectionId="content-health"
            eyebrow="Review health"
            title="Content health"
            subtitle="Annual review reminders based on each card's last reviewed date."
            action={
              <AppButton type="button" tone="outlinePrimary" size="sm" onPress={() => navigate("/admin/content")}>
                Review content
              </AppButton>
            }
            isOpen={Boolean(openSections["content-health"])}
            onToggle={() => toggleSection("content-health")}
          >
            <AppCardBody>
              <XStack flexWrap="wrap" gap="$sm">
                <AdminMetricCard label="Active cards" value={contentHealth.activeCards} />
                <AdminMetricCard label="Due now" value={contentHealth.dueNowCount} />
                <AdminMetricCard label="Due in 30 days" value={contentHealth.dueSoonCount} />
                <AdminMetricCard label="Missing review date" value={contentHealth.missingReviewDateCount} />
              </XStack>

              {contentHealth.highlightCards.length === 0 ? (
                <AppCard backgroundColor="$background" borderStyle="dashed">
                  <AppCardBody>
                    <AppText color="$colorMuted">No content cards yet.</AppText>
                  </AppCardBody>
                </AppCard>
              ) : (
                <AppSection>
                  {highlightTitle ? (
                    <XStack>
                      <AdminStatusPill tone="primary">{highlightTitle}</AdminStatusPill>
                    </XStack>
                  ) : null}
                  <YStack gap="$sm">
                    {contentHealth.highlightCards.map((card) => (
                      <AppCard key={card.id} backgroundColor="$background">
                        <AppCardBody>
                          <XStack alignItems="center" gap="$sm">
                            <YStack flex={1} minWidth={0} gap={4}>
                              <AppText fontSize="$sm" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
                                {card.title}
                              </AppText>
                              <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
                                {card.category} ·{" "}
                                {contentHealth.dueNowCount > 0
                                  ? describeContentHealthCard(card)
                                  : formatContentHealthDate(card.dueDate)}
                              </AppText>
                            </YStack>
                            <AppButton
                              type="button"
                              tone="outline"
                              size="sm"
                              onPress={() => navigate("/admin/content")}
                            >
                              Open
                            </AppButton>
                          </XStack>
                        </AppCardBody>
                      </AppCard>
                    ))}
                  </YStack>
                </AppSection>
              )}
            </AppCardBody>
          </AdminHomeSectionCard>

        </>
      )}
    </AdminPageScaffold>
  );
};

const AdminHomeSectionCard = ({
  sectionId,
  eyebrow,
  title,
  subtitle,
  action,
  accented = false,
  minWidth,
  isOpen,
  onToggle,
  children,
}: {
  sectionId: string;
  eyebrow: string;
  title: string;
  subtitle: string;
  action?: ReactNode;
  accented?: boolean;
  minWidth?: number;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
}) => {
  const panelId = `${sectionId}-admin-section-panel`;
  const toggleLabel = `${isOpen ? "Collapse" : "Expand"} ${title}`;

  return (
  <AppCard
    flexBasis={minWidth ? 0 : undefined}
    flexGrow={minWidth ? 1 : undefined}
    minWidth={minWidth}
    backgroundColor={accented ? "$primarySoft" : "$cardBackground"}
    borderColor={accented ? "$primarySoft" : "$borderColor"}
  >
    <AppCardHeader
      backgroundColor="$primarySoft"
      borderBottomWidth={isOpen && !accented ? 1 : 0}
      borderBottomColor="$borderColor"
    >
      <XStack alignItems="flex-start" gap="$sm">
        <TamaguiButton
          id={`${sectionId}-admin-section-toggle`}
          unstyled
          type="button"
          flex={1}
          alignItems="stretch"
          onPress={onToggle}
          pressStyle={{ opacity: 0.94 }}
          aria-controls={panelId}
          aria-expanded={isOpen}
          aria-label={toggleLabel}
        >
          <YStack gap="$xs">
            <XStack>
              <AdminStatusPill tone="primary">{eyebrow}</AdminStatusPill>
            </XStack>
            <AppSectionHeader title={title} subtitle={subtitle} />
          </YStack>
        </TamaguiButton>
        {action ? <XStack flexShrink={0}>{action}</XStack> : null}
        <TamaguiButton
          unstyled
          type="button"
          width={36}
          height={36}
          alignItems="center"
          justifyContent="center"
          borderWidth={1}
          borderRadius="$pill"
          borderColor="$borderColor"
          backgroundColor="$cardBackground"
          pressStyle={{ opacity: 0.92 }}
          onPress={onToggle}
          aria-controls={panelId}
          aria-expanded={isOpen}
          aria-label={toggleLabel}
        >
          <ChevronDown
            size={18}
            className="text-secondary transition-transform duration-200"
            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </TamaguiButton>
      </XStack>
    </AppCardHeader>
    {isOpen ? (
      <YStack id={panelId} aria-labelledby={`${sectionId}-admin-section-toggle`}>
        {children}
      </YStack>
    ) : null}
  </AppCard>
  );
};

const AdminActionRow = ({
  title,
  summary,
  count,
  actionLabel = "Open",
  onPress,
}: {
  title: string;
  summary: string;
  count?: number;
  actionLabel?: string;
  onPress: () => void;
}) => (
  <AppCard backgroundColor="$background">
    <AppCardBody>
      <XStack alignItems="center" gap="$sm">
        <YStack flex={1} minWidth={0} gap={4}>
          <XStack alignItems="center" gap="$xs">
            <AppText fontSize="$sm" lineHeight="$sm" fontFamily={kindredFontFamilies.sansBold}>
              {title}
            </AppText>
            {typeof count === "number" ? (
              <YStack
                minWidth={20}
                height={20}
                paddingHorizontal={6}
                borderRadius="$pill"
                backgroundColor={count > 0 ? "$primarySoft" : "$surface2"}
                alignItems="center"
                justifyContent="center"
                borderWidth={count > 0 ? 1 : 0}
                borderColor={count > 0 ? "$primary" : "transparent"}
              >
                <AppText
                  fontSize="$xs"
                  lineHeight="$xs"
                  fontFamily={kindredFontFamilies.sansBold}
                  color={count > 0 ? "$primary" : "$colorMuted"}
                >
                  {count}
                </AppText>
              </YStack>
            ) : null}
          </XStack>
          <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm">
            {summary}
          </AppText>
        </YStack>
        <AppButton type="button" tone="outlinePrimary" size="sm" onPress={onPress}>
          {actionLabel}
        </AppButton>
      </XStack>
    </AppCardBody>
  </AppCard>
);

export default AdminDashboard;
